import { useState } from 'react';
import {
  COMPANY_MAILBOX_KINDS, COMPANY_MAILBOX_KIND_LABEL_KEYS, Color, Size, companyMailboxEndPath, companyMailboxPath, companyMailboxesPath, normalizeEmail, t, validateEmail,
  type CompanyMailboxKindValue,
} from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Button, Select, TextInput } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import { patch, post } from './api';
import styles from './RecordPage.module.css';

/** The row a pencil opened, as its tile row carries it. */
export interface CompanyMailboxEdit {
  id: string;
  version: number;
  kind: CompanyMailboxKindValue;
  label: string | null;
  email: string;
}

/**
 * 🔒 The Company page's Mailboxes tile's + Add Mailbox and its pencil (decision
 * 2972; T2052): the kind, the row's own label and the address. A pencil
 * corrects an open row in place, naming the version it read, and ends it from
 * inside, behind a confirmation that says the row is kept as history, as a
 * contact line's Remove does (decision 0589).
 *
 * 🔨 Mounted only while open and keyed to the row, so a second row never opens on the first one's draft.
 */
export function CompanyMailboxModal({ companyId, initial = null, apiBaseUrl = '', onCancel, onSaved }: {
  companyId: string;
  initial?: CompanyMailboxEdit | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const editing = initial !== null;
  const [kind, setKind] = useState<CompanyMailboxKindValue>(initial?.kind ?? 'Sales');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setError(null);
    const body = { kind, label: label.trim() || null, email: email.trim() };
    const answer = editing ? await patch(companyMailboxPath(initial.id), { ...body, version: initial.version }, apiBaseUrl) : await post(companyMailboxesPath(companyId), body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };

  const end = async () => {
    if (!initial) return;
    setError(null);
    const answer = await post(companyMailboxEndPath(initial.id), { version: initial.version }, apiBaseUrl);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };

  return (
    <Modal
      open
      title={editing ? t('mailbox.modal.editTitle') : t('mailbox.modal.addTitle')}
      subtitle={t('mailbox.modal.subtitle')}
      onCancel={onCancel}
      dataModal={editing ? 'edit-company-mailbox' : 'add-company-mailbox'}
      actions={[
        { kind: 'primary', label: editing ? t('mailbox.saveEdit') : t('mailbox.save'), onAct: () => void save(), disabled: busy || email.trim() === '', disabledReason: t('mailbox.field.email') },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      <div data-company-mailbox-modal={editing ? 'edit' : 'add'}>
        {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
        <Select
          size={Size.Large}
          label={t('mailbox.field.kind')}
          required
          value={kind}
          onChange={(next) => setKind(next as CompanyMailboxKindValue)}
          options={COMPANY_MAILBOX_KINDS.map((option) => ({ value: option, label: t(COMPANY_MAILBOX_KIND_LABEL_KEYS[option]) }))}
          dataAttribute={{ 'data-company-mailbox-field': 'kind' }}
        />
        <TextInput size={Size.Medium} label={t('mailbox.field.label')} value={label} onChange={setLabel} placeholder={t('mailbox.field.labelPlaceholder')} name="company-mailbox-label" />
        <TextInput
          size={Size.Medium}
          label={t('mailbox.field.email')}
          type="email"
          value={email}
          onChange={setEmail}
          onBlur={() => setEmail((was) => normalizeEmail(was))}
          validate={(typed) => { if (!typed.trim()) return null; const outcome = validateEmail(typed, 'US'); return outcome.ok ? null : outcome.message; }}
          required
          name="company-mailbox-email"
        />
        {editing ? (
          <div className={styles.actions} data-company-mailbox-end="true">
            <Button
              size={Size.Small}
              color={Color.Destructive}
              text={[t('mailbox.end')]}
              actions={[() => void end()]}
              confirm={{ title: t('mailbox.endTitle'), consequence: { body: t('mailbox.endConsequence') }, confirmLabel: t('mailbox.endConfirm') }}
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
